import { Check, Close, Download, UploadFile } from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Grid,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import Cookies from "js-cookie";
import get from "lodash/get";
import Moment from "moment";
import { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useMutation } from "react-query";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import ProtectedPermissionPage from "../../components/ProtectedPermissionPage";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import service from "../../services/contract";
import { Link as RouterLink } from "react-router-dom";

interface Contract {
  id: number;
  employee_id: string;
  status: string;
  contract_start: string;
  contract_end: string;
}

interface EmployeeData {
  id: string;
  email: string;
  name: string;
  phone: string;
  salary: string;
  salary_unit: string;
  contract_end: string;
  contracts: Contract[];
}

interface ContractResponse {
  id: number;
  action: "approve" | "reject";
}

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      No contract!
    </Typography>
  </Box>
);

// Function to get status chip based on contract status
const getStatusChip = (status: string) => {
  let color: "success" | "warning" | "info" | "primary" | "error" | "default" =
    "default";
  let displayLabel = status;

  switch (status) {
    case "active":
      color = "success";
      displayLabel = "Active";
      break;
    case "employee_review":
      color = "warning";
      displayLabel = "Pending Employee Review";
      break;
    case "hr_review":
      color = "info";
      displayLabel = "Pending HR Review";
      break;
    case "finished":
      color = "primary";
      displayLabel = "Completed";
      break;
    case "pending_upload":
      color = "warning";
      displayLabel = "Pending Upload";
      break;
    case "cancelled":
      color = "error";
      displayLabel = "Cancelled";
      break;
    case "rejected":
      color = "error";
      displayLabel = "Rejected";
      break;
    default:
      color = "default";
      // Capitalize first letter and replace underscores with spaces
      displayLabel =
        status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, " ");
  }

  return <Chip label={displayLabel} color={color} size="small" />;
};

const EmployeeDashboard = (): JSX.Element => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [pageSize, setPageSize] = useState<number>(10);
  const alert = useAlert();
  const notification = useNotification();
  const user = JSON.parse(Cookies.get("USER") || "{}");
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [uploadingId, setUploadingId] = useState<number | null>(null);

  const [data, setData] = useState<EmployeeData>({
    id: user.id || "",
    email: "",
    name: "",
    phone: "",
    salary: "",
    salary_unit: "",
    contract_end: "",
    contracts: [],
  });

  const [options, setOptions] = useState<any>({
    keyword: "",
  });

  const downloadContract = useMutation(service.downloadContract, {
    onSuccess: () => {
      notification.onOpen({
        message: "Contract document was downloaded successfully!",
        type: "success",
        position: "top",
      });
    },
    onError: () => {
      notification.onOpen({
        message: "Failed to download contract",
        type: "error",
        position: "top",
      });
    },
  });

  const respondToContract = useMutation<any, Error, ContractResponse>(
    async ({ id, action }) => {
      const user = JSON.parse(Cookies.get("USER") || "{}");
      const isHR = user.role_id === 1;

      if (action === "approve") {
        // Choose API based on role
        if (isHR) {
          return service.hrApproveContract({
            id,
            hr_id: user.id,
            last_updated_by: user.name || user.username,
            role_id: user.role_id,
          });
        } else {
          return service.employeeApproveContract({
            id,
            employee_id: user.id,
            last_updated_by: user.name || user.username,
          });
        }
      } else {
        // Handle rejection based on role
        if (isHR) {
          return service.hrRejectContract({
            id,
            hr_id: user.id,
            rejection_reason: "Contract rejected by HR", // Ideally this would come from a dialog
            last_updated_by: user.name || user.username,
            role_id: user.role_id,
          });
        } else {
          return service.employeeRejectContract({
            id,
            employee_id: user.id,
            rejection_reason: "Contract rejected by employee", // Ideally this would come from a dialog
            last_updated_by: user.name || user.username,
          });
        }
      }
    },
    {
      onSuccess: (data) => {
        fetchEmployeeContracts();
        notification.onOpen({
          message: `Contract ${
            data.action === "approve" ? "approved" : "rejected"
          } successfully!`,
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        notification.onOpen({
          message: "Failed to respond to contract",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const uploadSignedContract = useMutation(
    (payload: { id: number; file: File }) =>
      service.uploadSignedContract(payload.id, payload.file),
    {
      onSuccess: () => {
        setUploadingId(null);
        fetchEmployeeContracts();
        notification.onOpen({
          message: "Signed contract uploaded successfully!",
          type: "success",
          position: "top",
        });
      },
      onError: () => {
        setUploadingId(null);
        notification.onOpen({
          message: "Failed to upload signed contract",
          type: "error",
          position: "top",
        });
      },
    }
  );

  const handleDownload = (contractId: number): void => {
    downloadContract.mutate(contractId);
  };

  const handleContractResponse = (
    contractId: number,
    action: "approve" | "reject"
  ): void => {
    // Use alert confirmation for rejection
    if (action === "reject") {
      alert.onOpen({
        title: "Confirm Rejection",
        message: "Are you sure you want to reject this contract?",
        actions: [
          {
            color: "error",
            variant: "contained",
            children: "Reject",
            onClick: () => {
              respondToContract.mutate({ id: contractId, action: "reject" });
              alert.onClose();
            },
          },
          {
            color: "inherit",
            variant: "outlined",
            children: "Cancel",
            onClick: () => alert.onClose(),
          },
        ],
      });
    } else {
      // Direct approval
      respondToContract.mutate({ id: contractId, action: "approve" });
    }
  };

  const handleUploadClick = (contractId: number) => {
    setUploadingId(contractId);
    fileInputRef.current?.click();
  };

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    contractId: number
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      uploadSignedContract.mutate({ id: contractId, file });
    }
    // Reset file input value so the same file can be selected again if needed
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const fetchEmployeeContracts = (): void => {
    service
      .getEmployeeContracts(user.id)
      .then((response: any) => {
        console.log(response);
        setData({
          id: user.id || "",
          email: response?.data?.employee?.email || "",
          name: response?.data?.employee?.name || "",
          phone: response?.data?.employee?.phone || "",
          salary: response?.data?.employee?.salary || "",
          salary_unit: response?.data?.employee?.salary_unit || "",
          contract_end: response?.data?.employee?.contract_end || "",
          contracts: response?.data?.contracts || [],
        });
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchEmployeeContracts();
  }, [user.id]);

  const columns: GridColDef[] = [
    {
      field: "created_time",
      headerName: "Date Created",
      flex: 1,
      filterable: false,
      minWidth: 150,
      renderCell: (params) => (
        <Typography>
          {Moment(params.value).format("YYYY-MM-DD hh:mm:ss")}
        </Typography>
      ),
    },
    {
      field: "contract_start",
      headerName: "Contract Start",
      flex: 1,
      filterable: false,
      minWidth: 150,
      renderCell: (params) => (
        <Typography>{Moment(params.value).format("YYYY-MM-DD")}</Typography>
      ),
    },
    {
      field: "contract_end",
      headerName: "Contract End",
      flex: 1,
      filterable: false,
      minWidth: 150,
      renderCell: (params) => (
        <Typography>{Moment(params.value).format("YYYY-MM-DD")}</Typography>
      ),
    },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      filterable: false,
      minWidth: 150,
      renderCell: (params) => getStatusChip(params.value as string),
    },
    {
      field: "actions",
      headerName: "Action",
      sortable: false,
      filterable: false,
      width: 260,
      renderCell: (params) => {
        const contract = params.row as Contract;
        const isEmployee = contract.employee_id === user.id;
        const isHR = user.role_id === 1;
        return (
          <Stack direction="row" spacing={1} alignItems="center">
            {contract.status === "employee_review" && isEmployee && (
              <>
                <Tooltip title="Approve">
                  <IconButton
                    color="success"
                    onClick={() =>
                      handleContractResponse(contract.id, "approve")
                    }
                  >
                    <Check />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject">
                  <IconButton
                    color="error"
                    onClick={() =>
                      handleContractResponse(contract.id, "reject")
                    }
                  >
                    <Close />
                  </IconButton>
                </Tooltip>
              </>
            )}

            <Button
              size="small"
              variant="contained"
              color="secondary"
              component={RouterLink}
              to={`/app/contract/detail/${contract.id}`}
            >
              Details
            </Button>
            {/* EMPLOYEE: Upload signed contract if pending_upload */}
            {contract.status === "pending_upload" && isEmployee && (
              <>
                <input
                  type="file"
                  style={{ display: "none" }}
                  ref={fileInputRef}
                  onChange={(e) => handleFileChange(e, contract.id)}
                  data-testid={`upload-input-${contract.id}`}
                />
                <Tooltip title="Upload Signed Contract">
                  <span>
                    <IconButton
                      color="primary"
                      onClick={() => handleUploadClick(contract.id)}
                      disabled={
                        uploadSignedContract.isLoading &&
                        uploadingId === contract.id
                      }
                    >
                      <UploadFile />
                    </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            {contract.status === "hr_review" && isHR && (
              <>
                <Tooltip title="Approve">
                  <IconButton
                    color="success"
                    onClick={() =>
                      handleContractResponse(contract.id, "approve")
                    }
                  >
                    <Check />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Reject">
                  <IconButton
                    color="error"
                    onClick={() =>
                      handleContractResponse(contract.id, "reject")
                    }
                  >
                    <Close />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {/* Download always available */}
            <Tooltip title="Download">
              <IconButton
                color="primary"
                onClick={() => handleDownload(contract.id)}
              >
                <Download />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ];

  return (
    <MainLayout>
      <Helmet>
        <title>Employee Detail</title>
      </Helmet>

      <Breadcrumb
        breadcrumbs={[]}
        label={"Employee Dashboard"}
        href=""
      />

      <ProtectedPermissionPage acceptPermissions={[1, 2]}>
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={{ lg: 4, md: 2 }}>
            <Grid item lg={4} md={12}>
              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Name
                </Typography>
                <Typography
                  variant="body1"
                  color="grey.600"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.name}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Email
                </Typography>
                <Typography
                  variant="body1"
                  color="grey.600"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.email}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Phone Number
                </Typography>
                <Typography
                  variant="body1"
                  color="grey.600"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.phone}
                </Typography>
              </Stack>
            </Grid>
            <Grid item lg={4} md={12}>
              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Salary
                </Typography>
                <Typography
                  variant="body1"
                  color="grey.600"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.salary}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Salary Unit
                </Typography>
                <Typography
                  variant="body1"
                  color="grey.600"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {data.salary_unit}
                </Typography>
              </Stack>

              <Stack sx={{ mt: 2 }}>
                <Typography variant="body1" color="grey.600" fontWeight="300">
                  Contract End
                </Typography>
                <Typography
                  variant="body1"
                  color="grey.600"
                  fontWeight="700"
                  sx={{ mt: 0.3 }}
                >
                  {Moment(data.contract_end).format("YYYY-MM-DD")}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ mt: 2 }}>
          <DataGrid
            autoHeight
            loading={isLoading}
            getRowId={(record) => get(record, "id")}
            rows={data.contracts}
            rowCount={data.contracts.length}
            pageSize={pageSize}
            rowsPerPageOptions={[10, 20, 30]}
            pagination
            disableSelectionOnClick
            onPageChange={(newPage) => {
              setOptions((prev: any) => ({ ...prev, page_number: newPage }));
            }}
            onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
            components={{
              NoRowsOverlay: CustomNoRowOverlay,
            }}
            columns={columns}
            sx={{
              ".MuiDataGrid-columnHeaderTitle": {
                fontWeight: 700,
              },
              ".MuiDataGrid-columnHeaderTitleContainer": {
                backgroundColor: "#22404a",
                color: "white",
              },
              ".MuiDataGrid-columnHeader": {
                backgroundColor: "#22404a",
                color: "white",
              },
              ".MuiDataGrid-columnSeparator": {
                backgroundColor: "#22404a",
                color: "white",
              },
            }}
          />
        </Box>
      </ProtectedPermissionPage>
    </MainLayout>
  );
};

export default EmployeeDashboard;
